import { mat3, ReadonlyMat3 } from 'gl-matrix';
import {
    IDENTITY_ROTATION,
    isPatientAligned,
} from '~/domain/viewport/frameRotation';
import { KeyedListeners } from '../core/KeyedListeners';
import type { Environment } from '../data/Environment';
import { PlaneView } from './views/PlaneView';

/**
 * The oblique frame of each Environment's views: one rotation in patient
 * space per Environment, applied on top of every view's base orientation, so
 * the views of one Environment turn together and stay mutually
 * perpendicular. A patient-aligned frame is not stored, so an Environment
 * holds a frame only while it is turned. The frame lives with the
 * Environment, not with its views: an Environment whose views all leave and
 * rejoin (a maximized tile, a plane cycle of every tile) keeps its frame and
 * turns the rejoining views, as it keeps its window and slice position.
 * Frames are held weakly, so a released Environment's frame goes with it,
 * and whether an Environment is turned is asked of it ({@link isTurned}).
 */
export class EnvironmentFrameRotations {
    private readonly frames = new WeakMap<Environment, mat3>();
    private readonly listeners = new KeyedListeners<Environment>();

    /** The frame of `environment`; identity while none is stored. */
    public frameOf(environment: Environment): ReadonlyMat3 {
        return this.frames.get(environment) ?? IDENTITY_ROTATION;
    }

    /** Whether `environment` holds a frame that is not patient-aligned. */
    public isTurned(environment: Environment): boolean {
        return this.frames.has(environment);
    }

    /**
     * Stores a copy of `rotation` as the frame of `environment` (or forgets
     * the frame when the rotation is patient-aligned), turns `views` to it
     * and tells the listeners. `views` are the Environment's own views.
     */
    public set(
        environment: Environment,
        rotation: ReadonlyMat3,
        views: readonly PlaneView[]
    ): void {
        if (isPatientAligned(rotation)) {
            this.frames.delete(environment);
        } else {
            this.frames.set(environment, mat3.clone(rotation));
        }
        for (const view of views) {
            this.apply(view);
        }
        this.listeners.notify(environment);
    }

    /**
     * Forgets the frame of `environment` and returns `views` to their base
     * orientation. Does nothing while no frame is stored, so views whose
     * frame is owned elsewhere are never re-oriented.
     * @returns Whether a frame was stored.
     */
    public clear(
        environment: Environment,
        views: readonly PlaneView[]
    ): boolean {
        if (!this.frames.has(environment)) {
            return false;
        }
        this.set(environment, IDENTITY_ROTATION, views);
        return true;
    }

    /**
     * Turns `view` to its Environment's stored frame, so a view that joins a
     * turned Environment shows the same frame as its siblings. Does nothing
     * while no frame is stored.
     */
    public applyStoredTo(view: PlaneView): void {
        if (this.frames.has(view.environment)) {
            this.apply(view);
        }
    }

    /**
     * Fires after the frame of `environment` is set or cleared.
     * @returns The unsubscribe.
     */
    public subscribe(
        environment: Environment,
        listener: () => void
    ): () => void {
        return this.listeners.subscribe(environment, listener);
    }

    /**
     * Sets `view`'s orientation to `frame * base`, the base being the view's
     * anatomical orientation on its Environment's image. An Environment
     * without an image, or a view without an anatomical label, has no base
     * orientation, so nothing is applied.
     */
    private apply(view: PlaneView): void {
        const label = view.label;
        if (!view.environment.image || label === undefined) {
            return;
        }
        view.applyFrameRotation(this.frameOf(view.environment), label);
    }
}
